/**
 * 60-месячный прогноз — чистые функции, вынесенные из `widgets/forecast/ForecastAmazonUS.tsx`.
 *
 * Это единственный источник формулы: компонент зовёт эти функции, golden-фикстуры
 * (`tests/golden/forecast60m.json`) считаются ими, а бэкенд-порт
 * (`backend/services/forecast_60m.py`) обязан давать те же числа на тех же фикстурах.
 * Правка формулы = правка здесь + регенерация фикстур + правка порта, в одном коммите.
 */

import { buildYearlyForecast, yearBounds, yearlyRowCount, type ForecastYearlyRow } from './forecastYearly';

export const FORECAST_MONTHS = 60;
export const MONTHLY_EXPENSES = 500;
export const DEPOSIT_PERCENT = 30;
export const REMAINING_PAYMENT_DELAY = 0;
export const PRODUCTION_WEEKS = 4;
export const DELIVERY_WEEKS = 2;
const WEEKS_PER_MONTH = 4;
/** Возврат пригодной к перепродаже единицы стоит ~15 % её цены. */
export const RETURN_PROCESSING_RATE = 0.15;
/** Amazon удерживает 20 % referral fee при возврате. */
export const REFERRAL_FEE_LOSS_RATE = 0.2;
/** Год, которого нет в плане продаж, растёт на 2 % в месяц. */
export const DEFAULT_YEARLY_MONTHLY_GROWTH = 0.02;
/** Месяцы 13+ ищут годовой темп по календарю: год = 2025 + floor((i − 1) / 12). */
export const SALES_PLAN_BASE_YEAR = 2025;
/** Первые 12 месяцев растут от базы (месяц 0), а не от предыдущего. */
export const FIRST_YEAR_GROWTH_MONTHS = 12;
const MONTHS_IN_YEAR = 12;

export type ForecastScenario = 'pessimistic' | 'realistic' | 'optimistic';
export const BASE_SALES_DEFAULT: Record<ForecastScenario, number> = { pessimistic: 35, realistic: 50, optimistic: 75 };
export const BASE_SALES_MULTIPLIER: Record<ForecastScenario, number> = { pessimistic: 0.7, realistic: 1.0, optimistic: 1.5 };

export interface ForecastCalculatorInputs {
  purchasePrice: number;
  shippingPrice: number;
  storageFee: number;
  sellingPrice: number;
  referralFee: number;
  fbaFee: number;
  otherFees: number;
  ppcCost: number;
  conversionRate: number;
  ppcSalesPercentage: number;
  returnRate: number;
  resellableReturns: number;
  monthlySales: number;
  batchQuantity: number;
}

export interface ForecastSalesPlan {
  salesGrowth: { year: number; monthlyGrowth: number }[];
  monthlyGrowth2025: { month: number; growth: number }[];
  tacos: { month: number; tacos: number }[];
  targetTacos: number;
}

/** Индекс месяца → продажи, введённые вручную. */
export type SalesOverrides = Record<number, number>;

export interface MonthInput {
  sales: number;
  price: number;
}

export interface ForecastMonthRow {
  sales: number;
  price: number;
  revenue: number;
  cumRevenue: number;
  allFees: number;
  ppcSalesPercent: number;
  adSpend: number;
  returnCosts: number;
  invCurrent: number;
  invOrder: number | null;
  monthlyProfit: number;
  profitWithoutAds: number;
  pl: number;
  cumPL: number;
  cfMonthly: number;
  cfCum: number;
  payments: number;
  margin: number;
  marginWithoutAds: number;
  monthlyROI: number;
  cumROI: number;
  cumInvValue: number;
}

export interface ForecastSummary {
  cogsPerUnit: number;
  sellingPrice: number;
  positiveMonthPL: number;
  positiveMonthCF: number;
  minCashFlow: number;
  monthlyData: {
    month: string;
    revenue: number;
    profit: number;
    pl: number;
    cumPL: number;
    cashFlow: number;
    cumCF: number;
  }[];
  yearlyData: ForecastYearlyRow[];
  totalData: {
    revenue: number;
    pl: number;
    cashFlow: number;
    adSpend: number;
    fixedCosts: number;
    profit: number;
    profitWithoutAds: number;
    avgMargin: number;
    avgROI: number;
  };
}

export interface Forecast60mInput {
  calculator: ForecastCalculatorInputs;
  salesPlan: ForecastSalesPlan;
  launchBudget: number;
  scenario: ForecastScenario;
  salesOverrides: SalesOverrides;
  /** YYYY-MM-DD первого месяца прогноза. */
  startDate: string;
}

export interface Forecast60mResult {
  months: ForecastMonthRow[];
  summary: ForecastSummary;
}

export function baseSales(monthlySales: number, scenario: ForecastScenario): number {
  if (monthlySales > 0) return Math.round(monthlySales * BASE_SALES_MULTIPLIER[scenario]);
  return BASE_SALES_DEFAULT[scenario];
}

function yearlyRate(plan: ForecastSalesPlan, monthIndex: number): number {
  const calendarYear = SALES_PLAN_BASE_YEAR + Math.floor((monthIndex - 1) / MONTHS_IN_YEAR);
  const year = plan.salesGrowth.find((s) => s.year === calendarYear);
  return year ? year.monthlyGrowth / 100 : DEFAULT_YEARLY_MONTHLY_GROWTH;
}

function firstYearRate(plan: ForecastSalesPlan, monthIndex: number): number {
  const month = plan.monthlyGrowth2025.find((m) => m.month === monthIndex);
  return month ? month.growth / 100 : 0;
}

/**
 * Продажи по месяцам: месяц 0 — база, 1..12 — от базы по помесячному плану, 13+ —
 * от предыдущего месяца по годовому темпу, с округлением на каждом шаге. Ручные
 * значения держатся как есть и служат базой для следующих месяцев (13+).
 */
export function buildSalesSchedule(plan: ForecastSalesPlan, base: number, overrides: SalesOverrides): number[] {
  const sales: number[] = new Array<number>(FORECAST_MONTHS);
  sales[0] = overrides[0] ?? base;
  for (let i = 1; i < FORECAST_MONTHS; i++) {
    const manual = overrides[i];
    if (manual !== undefined) {
      sales[i] = manual;
    } else if (i <= FIRST_YEAR_GROWTH_MONTHS) {
      sales[i] = Math.round(sales[0] * (1 + firstYearRate(plan, i)));
    } else {
      sales[i] = Math.round(sales[i - 1] * (1 + yearlyRate(plan, i)));
    }
  }
  return sales;
}

interface OrderPlan {
  orderMonth: number;
  deliveryMonth: number;
  quantity: number;
}

interface InventorySnapshot {
  current: number;
  order: number | null;
}

function simulateInventory(months: MonthInput[], batchQty: number): InventorySnapshot[] {
  let inv = batchQty;
  const orders: OrderPlan[] = [];
  const leadTimeMonths = Math.ceil((PRODUCTION_WEEKS + DELIVERY_WEEKS) / WEEKS_PER_MONTH);
  const last = FORECAST_MONTHS - 1;
  const snapshots: InventorySnapshot[] = [];

  for (let i = 0; i < FORECAST_MONTHS; i++) {
    const sales = months[i]?.sales ?? 0;
    inv -= sales;
    for (const o of orders) {
      if (o.deliveryMonth === i) inv += o.quantity;
    }

    if (i < last) {
      const forecastPeriod = leadTimeMonths + 1;
      let futureSales = 0;
      for (let j = i + 1; j <= Math.min(i + forecastPeriod, last); j++) {
        futureSales += months[j]?.sales ?? months[i]?.sales ?? 0;
      }
      let incomingStock = 0;
      for (const o of orders) {
        if (o.deliveryMonth > i && o.deliveryMonth <= i + forecastPeriod) incomingStock += o.quantity;
      }

      if (inv + incomingStock < futureSales) {
        let orderQty = futureSales - (inv + incomingStock);
        for (let j = i + forecastPeriod + 1; j <= Math.min(i + forecastPeriod + 3, last); j++) {
          orderQty += months[j]?.sales ?? months[i]?.sales ?? 0;
        }
        orderQty = Math.max(orderQty, batchQty);
        orders.push({ orderMonth: i, deliveryMonth: i + leadTimeMonths, quantity: orderQty });
      }
    }

    const currentOrder = orders.find((o) => o.orderMonth === i);
    snapshots.push({ current: Math.max(inv, 0), order: currentOrder ? currentOrder.quantity : null });
  }
  return snapshots;
}

export function computeForecastMonths(
  months: MonthInput[],
  calc: ForecastCalculatorInputs,
  plan: ForecastSalesPlan,
  launchBudget: number,
): ForecastMonthRow[] {
  const unitCogs = calc.purchasePrice + calc.shippingPrice;
  const batchQty = calc.batchQuantity;
  const firstOrderAmount = batchQty * unitCogs;
  const baseUnitPrice = months[0]?.price ?? calc.sellingPrice;
  const unitReferralFee = baseUnitPrice * (calc.referralFee / 100);
  const unitFbaFee = calc.fbaFee;
  // «Прочие» в калькуляторе — процент от цены (unitEconomics.ts, budget.ts).
  const unitOtherFees = baseUnitPrice * (calc.otherFees / 100);
  const unitStorageFee = calc.storageFee;

  const invSnapshots = simulateInventory(months, batchQty);
  const rows: ForecastMonthRow[] = [];
  let cumPL = 0;
  let cumCF = 0;
  let cumRevenue = 0;
  let cumInvValue = firstOrderAmount;

  for (let mi = 0; mi < FORECAST_MONTHS; mi++) {
    const sales = months[mi]?.sales ?? 0;
    const price = months[mi]?.price ?? 0;
    const returnRate = calc.returnRate / 100;
    // Возвращённые единицы — возврат денег, выручки по ним нет.
    const revenue = sales * price * (1 - returnRate);
    cumRevenue += revenue;

    const ppcSalesPercent = mi < plan.tacos.length
      ? plan.tacos[mi].tacos
      : (calc.ppcSalesPercentage ?? plan.targetTacos);

    // Конверсия — делитель; ноль означает «не задана», и рекламы в прогнозе нет.
    const ppcSales = sales * (ppcSalesPercent / 100);
    const conversionRate = calc.conversionRate > 0 ? calc.conversionRate / 100 : 0;
    const adSpend = conversionRate > 0 ? (ppcSales / conversionRate) * calc.ppcCost : 0;

    const allFees = sales * (unitReferralFee + unitFbaFee + unitOtherFees + unitStorageFee);

    const resellableRate = calc.resellableReturns / 100;
    const lostRate = 1 - resellableRate;
    const returned = sales * returnRate;
    const lostUnitsCost = returned * lostRate * unitCogs;
    const returnProcessingCost = returned * resellableRate * price * RETURN_PROCESSING_RATE;
    const referralFeeLoss = returned * unitReferralFee * REFERRAL_FEE_LOSS_RATE;
    const returnCosts = lostUnitsCost + returnProcessingCost + referralFeeLoss;

    const monthlyProfit = revenue - allFees - adSpend - sales * unitCogs - returnCosts;
    const profitWithoutAds = revenue - allFees - sales * unitCogs - returnCosts;
    let pl = monthlyProfit - MONTHLY_EXPENSES;
    if (mi === 0) pl -= launchBudget;
    cumPL += pl;

    // Каждая партия: депозит в месяц заказа, остаток через REMAINING_PAYMENT_DELAY месяцев.
    // При нулевой задержке партия оплачивается целиком в месяц заказа.
    const depositShare = DEPOSIT_PERCENT / 100;
    const remainderShare = (100 - DEPOSIT_PERCENT) / 100;
    let payments = 0;
    if (mi === 0) payments += firstOrderAmount * depositShare;
    if (mi === REMAINING_PAYMENT_DELAY) payments += firstOrderAmount * remainderShare;

    const invData = invSnapshots[mi];
    if (invData.order) {
      payments += invData.order * unitCogs * depositShare;
      cumInvValue += invData.order * unitCogs;
    }
    if (mi >= REMAINING_PAYMENT_DELAY) {
      const orderInv = invSnapshots[mi - REMAINING_PAYMENT_DELAY];
      if (orderInv.order) payments += orderInv.order * unitCogs * remainderShare;
    }

    const cfMonthly = pl - payments;
    cumCF += cfMonthly;

    const margin = revenue > 0 ? (monthlyProfit / revenue) * 100 : 0;
    const marginWithoutAds = revenue > 0 ? (profitWithoutAds / revenue) * 100 : 0;
    const totalInv = cumInvValue + launchBudget;
    const cumROI = totalInv > 0 ? (cumPL / totalInv) * 100 : 0;
    const monthlyCost = sales * unitCogs + allFees + adSpend + returnCosts + MONTHLY_EXPENSES;
    const monthlyROI = monthlyCost > 0 ? (monthlyProfit / monthlyCost) * 100 : 0;

    rows.push({
      sales, price, revenue, cumRevenue, allFees, ppcSalesPercent, adSpend, returnCosts,
      invCurrent: invData.current, invOrder: invData.order,
      monthlyProfit, profitWithoutAds, pl, cumPL, cfMonthly, cfCum: cumCF, payments,
      margin, marginWithoutAds, monthlyROI, cumROI, cumInvValue,
    });
  }
  return rows;
}

function parseStart(startDate: string): { year: number; monthIndex: number } {
  const [y, m] = startDate.split('-').map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m) || m < 1 || m > 12) {
    throw new Error(`forecast60m: startDate должен быть YYYY-MM-DD, получено «${startDate}»`);
  }
  return { year: y, monthIndex: m - 1 };
}

/** 60 подписей вида `YYYY-MM`, начиная с месяца даты старта. */
export function monthLabelsFrom(startDate: string): string[] {
  const { year, monthIndex } = parseStart(startDate);
  const labels: string[] = [];
  let y = year;
  let mi = monthIndex;
  for (let i = 0; i < FORECAST_MONTHS; i++) {
    labels.push(`${y}-${String(mi + 1).padStart(2, '0')}`);
    mi++;
    if (mi === MONTHS_IN_YEAR) {
      mi = 0;
      y++;
    }
  }
  return labels;
}

/** Подпись строки года — календарный год её первого месяца (границы строк — `forecastYearly.ts`). */
export function yearLabelsFrom(startDate: string): string[] {
  const months = monthLabelsFrom(startDate);
  const labels: string[] = [];
  for (let yi = 0; yi < yearlyRowCount(FORECAST_MONTHS); yi++) {
    labels.push(months[yearBounds(yi).start].slice(0, 4));
  }
  return labels;
}

export function summarizeForecast(
  months: ForecastMonthRow[],
  opts: { launchBudget: number; sellingPrice: number; cogsPerUnit: number; monthLabels: string[]; yearLabels: string[] },
): ForecastSummary {
  const yearlyData = buildYearlyForecast({
    months,
    yearLabels: opts.yearLabels,
    monthlyExpenses: MONTHLY_EXPENSES,
    launchBudget: opts.launchBudget,
  });

  let posMonthPL = 0;
  let posMonthCF = 0;
  let minCF = 0;
  for (let i = 0; i < months.length; i++) {
    if (!posMonthPL && months[i].pl > 0) posMonthPL = i + 1;
    if (!posMonthCF && months[i].cfCum > 0) posMonthCF = i + 1;
    if (months[i].cfCum < minCF) minCF = months[i].cfCum;
  }

  const monthlyData = months.map((p, i) => ({
    month: opts.monthLabels[i],
    revenue: Math.round(p.revenue),
    profit: Math.round(p.monthlyProfit),
    pl: Math.round(p.pl),
    cumPL: Math.round(p.cumPL),
    cashFlow: Math.round(p.cfMonthly),
    cumCF: Math.round(p.cfCum),
  }));

  const tRev = yearlyData.reduce((s, y) => s + y.revenue, 0);
  const tPL = yearlyData.reduce((s, y) => s + y.pl, 0);
  const tCF = yearlyData.reduce((s, y) => s + y.cashFlow, 0);
  const tAd = yearlyData.reduce((s, y) => s + y.adSpend, 0);
  const tFC = yearlyData.reduce((s, y) => s + y.fixedCosts, 0);
  const tProfit = yearlyData.reduce((s, y) => s + y.profit, 0);
  const tProfitNoAds = yearlyData.reduce((s, y) => s + y.profitWithoutAds, 0);
  const tInv = months[months.length - 1].cumInvValue + opts.launchBudget;

  return {
    cogsPerUnit: opts.cogsPerUnit,
    sellingPrice: opts.sellingPrice,
    positiveMonthPL: posMonthPL,
    positiveMonthCF: posMonthCF,
    minCashFlow: Math.round(minCF),
    monthlyData,
    yearlyData,
    totalData: {
      revenue: Math.round(tRev),
      pl: Math.round(tPL),
      cashFlow: Math.round(tCF),
      adSpend: Math.round(tAd),
      fixedCosts: Math.round(tFC),
      profit: Math.round(tProfit),
      profitWithoutAds: Math.round(tProfitNoAds),
      avgMargin: tRev > 0 ? Math.round((tProfit / tRev) * 1000) / 10 : 0,
      avgROI: tInv > 0 ? Math.round((tPL / tInv) * 1000) / 10 : 0,
    },
  };
}

export function computeForecast60m(input: Forecast60mInput): Forecast60mResult {
  const base = baseSales(input.calculator.monthlySales, input.scenario);
  const sales = buildSalesSchedule(input.salesPlan, base, input.salesOverrides);
  const months = computeForecastMonths(
    sales.map((s) => ({ sales: s, price: input.calculator.sellingPrice })),
    input.calculator,
    input.salesPlan,
    input.launchBudget,
  );
  const summary = summarizeForecast(months, {
    launchBudget: input.launchBudget,
    sellingPrice: input.calculator.sellingPrice,
    cogsPerUnit: input.calculator.purchasePrice + input.calculator.shippingPrice,
    monthLabels: monthLabelsFrom(input.startDate),
    yearLabels: yearLabelsFrom(input.startDate),
  });
  return { months, summary };
}
